import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "../context/LanguageContext";
import { T } from "../i18n/translations";
import { CONTACT } from "../data/media";

const links = [
  { to: "/", key: "home" },
  { to: "/menu", key: "menu" },
  { to: "/storia", key: "storia" },
  { to: "/contatti", key: "contatti" },
];

export const Navbar = () => {
  const { lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-colors duration-500 ${
          scrolled ? "bg-trise-ivory/85 backdrop-blur-xl border-b border-trise-brass/30" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Logo dark={!scrolled} />

          <nav className="hidden md:flex items-center gap-10">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.key}`}
                className={({ isActive }) =>
                  `text-xs uppercase tracking-widen font-medium transition-colors duration-300 ${
                    isActive
                      ? "text-trise-terra"
                      : scrolled
                      ? "text-trise-ink hover:text-trise-terra"
                      : "text-trise-ivory hover:text-trise-brass"
                  }`
                }
              >
                {T.nav[l.key][lang]}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4 md:gap-6">
            <button
              onClick={toggle}
              data-testid="lang-toggle"
              className="text-xs uppercase tracking-widen font-bold transition-colors"
              aria-label="Toggle language"
            >
              <span className={lang === "it" ? "text-trise-terra" : scrolled ? "text-trise-ink/50" : "text-trise-ivory/60"}>IT</span>
              <span className="mx-1 text-trise-brass">/</span>
              <span className={lang === "en" ? "text-trise-terra" : scrolled ? "text-trise-ink/50" : "text-trise-ivory/60"}>EN</span>
            </button>

            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-reserve"
              className={`hidden md:inline-block px-6 py-3 text-xs uppercase tracking-widen font-bold transition-colors duration-300 hover:bg-trise-terra hover:text-trise-ivory ${
                scrolled ? "bg-trise-blue text-trise-ivory" : "bg-trise-brass text-trise-blue"
              }`}
            >
              {T.nav.reserve[lang]}
            </a>

            <button
              onClick={() => setOpen(true)}
              className={`md:hidden ${scrolled ? "text-trise-ink" : "text-trise-ivory"}`}
              data-testid="mobile-menu-open"
              aria-label="Open menu"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] bg-trise-blue text-trise-ivory md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 h-20">
              <Logo dark />
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu">
                <X size={28} className="text-trise-ivory" />
              </button>
            </div>
            <nav className="flex flex-col px-6 mt-10 gap-2">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.key}`}
                    className="block py-4 text-4xl font-bold border-b border-trise-brass/30"
                  >
                    {T.nav[l.key][lang]}
                    <span className="block text-sm italic font-normal text-trise-brass">
                      {T.nav[l.key][lang === "it" ? "en" : "it"]}
                    </span>
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="px-6 mt-10">
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center bg-trise-brass text-trise-blue px-6 py-4 text-sm uppercase tracking-widen font-bold"
                data-testid="mobile-reserve"
              >
                {T.nav.reserve[lang]}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
